// LinkSubstring.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import UsernameTag from '../container/UsernameTag.js';
import LinkSubstringOembed from '../container/LinkSubstringOembed.js';
import { getIsChinaPartner } from '../resource/partner.js';
import DiamondIconSrc from '../../img/ic_diamond_lightgrayline.svg';
import LinkSubstringHashtag from '../container/LinkSubstringHashtag.js';

const stamp = `__${uuidv4()}__`; // Change stamp from `uuid` to `__${uuid}__` to prevent there is no divider(like: space) between hashtag and other content
const isChinaPartner = getIsChinaPartner();
const usernameRegex = /@[.\w_]+/g; // TODO: remote config
const emailRegex = // TODO: remote config
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/gi;
const urlRegex = // TODO: remote config
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/gi;
const relativeUrlRegex = / \/[\S]+ /gi; // TODO: remote config

// we parsing the string and replace url, @username, newline, and space to
const LinkSubstring = ({
  children = '',
  shouldHandleRelativeUrl = false,
  shouldUseLink = true,
  hashtagRegexPattern = '',
}) => {
  if (typeof children !== 'string' && !(children instanceof String)) {
    return children;
  }

  const hashtagRegex = hashtagRegexPattern
    ? new RegExp(hashtagRegexPattern, 'gm')
    : null;

  let usernames = children.match(usernameRegex);
  let hashtags = children.match(hashtagRegex);
  let emails = children.match(emailRegex);
  let urls = children.match(urlRegex);
  let relativeUrls = [];
  if (shouldHandleRelativeUrl) {
    relativeUrls = children.match(relativeUrlRegex);
  }

  const replacedStrings = children
    .replace(emailRegex, `${stamp}REPLACED_EMAIL${stamp}`)
    .replace(urlRegex, `${stamp}REPLACED_URL${stamp}`)
    .replace(usernameRegex, `${stamp}REPLACED_USERNAME${stamp}`)
    .replace(hashtagRegex, `${stamp}REPLACED_HASHTAG${stamp}`)
    .replace(relativeUrlRegex, `${stamp}RELATIVE_URL${stamp}`)
    .replace(/\n/g, `${stamp}BR${stamp}`)
    .replace(/\s/g, `${stamp}SPACE${stamp}`)
    .replace(/__IMAGE_DIAMOND__/g, `${stamp}REPLACED_IMAGE_DIAMOND${stamp}`)
    .split(stamp);

  return replacedStrings.map((str, index) => {
    if (str === 'SPACE') {
      return ' ';
    }

    if (str === 'BR') {
      return shouldUseLink ? <br key={index} /> : ' ';
    }

    let result = str;

    if (str === 'REPLACED_USERNAME') {
      const username = usernames.shift();
      if (username)
        result = shouldUseLink ? (
          <UsernameTag key={index} username={username.substring(1)} />
        ) : (
          <StyledNoLink key={index}>{username}</StyledNoLink>
        );
    }

    if (str === 'REPLACED_HASHTAG') {
      const hashtag = hashtags.shift();
      if (hashtag)
        result = shouldUseLink ? (
          <LinkSubstringHashtag key={index} hashtag={hashtag} />
        ) : (
          <StyledNoLink key={index}>{hashtag}</StyledNoLink>
        );
    }

    if (str === 'REPLACED_EMAIL') {
      const email = emails.shift();

      if (email && isChinaPartner) {
        result = email;
      }
      if (email) {
        result = (
          <UrlStyled key={index} href={`mailto:${email}`}>
            {email}
          </UrlStyled>
        );
      }
    }

    if (str === 'REPLACED_IMAGE_DIAMOND') {
      result = (
        <img
          key={index}
          src={DiamondIconSrc.src || DiamondIconSrc}
          alt="diamond"
        />
      );
    }

    if (str === 'REPLACED_URL') {
      const paredUrl = urls.shift();
      if (paredUrl) {
        const url = new URL(paredUrl);
        result = (
          <LinkSubstringOembed
            key={index}
            url={url.href}
            shouldUseLink={shouldUseLink}
          />
        );
      }
    }

    if (str === 'RELATIVE_URL') {
      const relativeUrl = relativeUrls.shift()?.trim();
      result = (
        <LinkSubstringOembed
          key={index}
          url={relativeUrl}
          shouldUseLink={shouldUseLink}
        />
      );
    }

    if (replacedStrings[index - 1] && replacedStrings[index - 1] !== 'SPACE') {
      result = <React.Fragment key={index}> {result}</React.Fragment>;
    }

    return result;
  });
};

const UrlStyled = styled.a`
  color: #00d2be;
`;

const StyledNoLink = styled.span`
  color: #00d2be;
  font-size: inherit;
  font-weight: inherit;
`;

LinkSubstring.propTypes = {
  children: PropTypes.node,
  hashtagRegexPattern: PropTypes.string,
  shouldUseLink: PropTypes.bool,
};

export default LinkSubstring;
